import { datadogLogs } from '@datadog/browser-logs'
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { z } from 'zod'
import { fromInteger } from '@repo/money'
import type {
  BalanceDTO,
  BetDTO,
  SocialBetDTO,
  SocialBetSnapshotItemDTO,
  RoundSnapshotDTO
} from 'src/actions/types'
import { isIdleBannerDisabled } from 'src/components/IdleBannerWrapper/utils/isIdleBannerDisabled'
import type { DailyCheckInActions } from 'src/features/dailyCheckIn'
import { fromDailyStreakDTO } from 'src/features/dailyCheckIn/mappers'
import { DailyStreakSchema } from 'src/features/dailyCheckIn/types/schema'
import { fromInventoryItemDTO } from 'src/features/inventory/mappers/inventory.mapper'
import type { InventoryActions } from 'src/features/inventory/store'
import { InventoryItemDtoSchema } from 'src/features/inventory/types/schema'
import { inventoryItemToNotification, levelUpToNotification } from 'src/features/notification'
import type { NotificationActions } from 'src/features/notification/store'
import { useBetStore, type BetActions } from 'src/store/bet'
import { routeBirdieProgressRow } from 'src/store/birdiePutts'
import {
  claimBettingClosedPost,
  isHardRoundHeard,
  isHeardRow,
  noteRaceEventHeard
} from 'src/store/raceEventHeard'
import { usePlayerStore } from 'src/store/player'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import type { GameActions } from 'src/store/game'
import type { PlayerActions } from 'src/store/player'
import type { SocialActions } from 'src/store/social'
import type { SocialStreamActions } from 'src/store/socialStream'
import type { SocketActions } from 'src/store/socket'
import type { StreakActions } from 'src/store/streak'
import { usePlayerLevelStore, type PlayerLevelActions } from 'src/store/playerLevel'
import type { UIActions } from 'src/store/ui'
import { getBetsByGameAndPlayer } from 'src/store/utils/getBetsByGameAndPlayer'
import {
  isRaceCancelledEvent,
  isRaceReadyEvent,
  isRaceSettledEvent
} from 'src/types/domain/RaceEvent'
import { RaceConfigurationRowDTOSchema } from 'src/types/infra/supabase/RaceConfigurationRowDTO'
import { RaceEventRowDTOSchema } from 'src/types/infra/supabase/RaceEventRowDTO'
import { RaceRowDTOSchema } from 'src/types/infra/supabase/RaceRowDTO'
import {
  SupabaseInsertEventDTOSchema,
  SupabaseUpdateEventDTOSchema
} from 'src/types/infra/supabase/SupabaseEventDTO'
import { TrackModeRowDTOSchema } from 'src/types/infra/supabase/TrackModeRowDTO'
import { TrackRowDTOSchema } from 'src/types/infra/supabase/TrackRowDTO'
import { BetMap } from 'src/types/mappers/BetMap'
import { GameMap } from 'src/types/mappers/GameMap'
import { RaceMap } from 'src/types/mappers/NormalizedRaceMap'
import { RaceEventMap } from 'src/types/mappers/RaceEventMap'
import { SocialBetMap } from 'src/types/mappers/SocialBetMap'
import { SocialStreamMap } from 'src/types/mappers/SocialStreamMap'
import { settleBetsForRace } from './settleBetsForRace'
import { fetchServerBetsSnapshot } from './fetchServerBetsSnapshot'
import { lobbyPath } from './lobbyPath'
import {
  emitBetPlaced,
  emitBetRejected,
  emitBetResult,
  emitCashoutCompleted,
  emitGameReady,
  emitRoundBettingOpen,
  emitRoundBettingClosed,
  emitRoundStarted,
  emitRoundEnded,
  emitBalanceInsufficient,
  emitPlayerIdleWarning,
  emitPlayerIdlePaused
} from 'src/services/postMessage'

export interface Actions {
  bet: BetActions
  player: PlayerActions
  ui: UIActions
  game: GameActions
  social: SocialActions
  socket: SocketActions
  socialStream: SocialStreamActions
  inventory?: InventoryActions
  notification?: NotificationActions
  dailyCheckin: DailyCheckInActions
  streak?: StreakActions
  playerLevel?: PlayerLevelActions
}

// BETS
export const handleBetAccepted =
  (actions: Actions, updateBalance: () => Promise<void>) => (data: unknown) => {
    // console.info('[useWebSockets] bet-accepted', data)

    // ! TODO: use zod to validate data

    const payload = (data as { data: { bet: BetDTO; balance: BalanceDTO } }).data
    const bet = BetMap.fromBetDTO(payload.bet)

    datadogLogs.logger.info('bet_accepted', {
      user_event: {
        bet: payload.bet,
        balance: payload.balance
      }
    })

    // update state
    actions.bet.betAccepted(bet)

    // emit to parent iframe
    emitBetPlaced({
      betId: bet.id,
      amount: bet.betAmount,
      currency: bet.currency,
      betType: bet.betDetails.type
    })

    // fetch new balance
    void updateBalance()
  }

export const handleBetRejected =
  (actions: Actions, updateBalance: () => Promise<void>) => (data: unknown) => {
    // console.info('[useWebSockets] bet-rejected', data)

    // ! TODO: use zod to validate data
    const payload = (data as { data: { input: BetDTO; reason: string; detail?: string } }).data
    const errorType = payload.reason
    const betDTO = payload.input

    // The hard close's twins rule (DESIGN §6): on a hard round a rejection for
    // a bet this page already holds as accepted (✓) is a stale twin, and is
    // ignored. Every other round is handled as it always was.
    const held = useBetStore.getState()
    const acceptedBet =
      betDTO?.id !== undefined && held.unacceptedBets[betDTO.id] === undefined
        ? held.bets.byId[betDTO.id]
        : undefined
    if (acceptedBet && isHardRoundHeard(acceptedBet.raceId)) {
      datadogLogs.logger.info('bet_rejected_twin_ignored', {
        user_event: { betId: betDTO.id, reason: errorType, detail: payload.detail }
      })
      return
    }

    // `detail` tells a hard round's late answer (RoundClosed, NotInTime) from
    // any other RoundClosed.
    datadogLogs.logger.info('bet_rejected', {
      user_event: {
        bet: payload.input,
        reason: payload.reason,
        detail: payload.detail
      }
    })

    // Console breadcrumb for dev/QA — datadog logs are great for
    // production triage but we also want to see the rejection
    // reason instantly in the browser console while iterating. We
    // dump the full `input` payload alongside the cherry-picked
    // fields because the wire shape can vary by submission path
    // (chip-placement uses PlaceBetRequestedDTO, classic mode uses
    // BetDTO, etc.) and falling back to the full object surfaces
    // any field name mismatches at a glance.
    console.warn('[ws] bet:rejected', {
      reason: errorType,
      betId: betDTO?.id,
      amount: betDTO?.amount ?? (betDTO as unknown as { betAmount?: number })?.betAmount,
      currency: betDTO?.currency,
      selectedItems: betDTO?.details?.selectedItems,
      fullInput: betDTO
    })

    // Pull the original bet out of `unacceptedBets` BEFORE calling
    // `actions.bet.betRejected(...)` — that call removes the entry.
    // The optimistic balance debit happened in major-currency units
    // via `playerActions.betPlaced(bet, false)`, so the refund must
    // be in matching units. `betDTO.amount` from the rejection echo
    // is unreliable: chip-placement bets travel through the Go
    // `RegisterBetCommandData` aggregate where `Amount uint64` is
    // tagged `json:"-"` (no custom MarshalJSON), so the field is
    // stripped before it reaches the realtime broadcast and arrives
    // here as `undefined` — leading to skipped refunds and balances
    // that visually stay debited until the next `updateBalance()`
    // tick. The stored bet's `betAmount` is authoritative.
    const trackedBet = useBetStore.getState().unacceptedBets[betDTO.id]

    // update state
    actions.bet.betRejected(betDTO.id, errorType, payload.detail)
    if (trackedBet?.betAmount !== undefined) {
      // Preferred path — refund in major units sourced from the
      // tracked bet (correct for chip-placement and any future flow
      // that goes through `unacceptedBets`).
      actions.player.betRejected(trackedBet.betAmount)
    } else {
      // Fallback for legacy callers where the tracked bet isn't
      // available — keeps classic-mode behavior unchanged. The
      // store's `betRejected` already guards against undefined.
      actions.player.betRejected(betDTO.amount)
    }
    // A hard round's late answer (Go: RoundClosed, detail NotInTime) is told
    // as "not in time, stake returning"; the reason posted to the operator
    // below is the reason as it came.
    actions.ui.betRejected(errorType, payload.detail)

    // emit to parent iframe
    emitBetRejected({
      reason: errorType,
      amount: betDTO.amount != null ? fromInteger(betDTO.amount, betDTO.currency) : 0,
      currency: betDTO.currency
    })

    if (errorType === 'NotEnoughMoney') {
      const player = usePlayerStore.getState().currentPlayer
      if (isAuthenticatedPlayer(player)) {
        emitBalanceInsufficient({
          requiredAmount: fromInteger(betDTO.amount, betDTO.currency),
          currentBalance: player.balance.amount,
          currency: player.balance.currency
        })
      }
    }

    // fetch new balance
    void updateBalance()
  }

export const handleBetWon =
  (actions: Actions, updateBalance: () => Promise<void>) => (data: unknown) => {
    // console.info('[useWebSockets] betWon', data)

    const betWonDTO = (data as { data: { bet: BetDTO; balance: BalanceDTO } }).data
    const bet = BetMap.fromBetDTO(betWonDTO.bet)

    // update state
    actions.player.betWon(bet)
    actions.bet.betWon(bet)

    // emit to parent iframe
    emitBetResult({
      betId: bet.id,
      status: 'won',
      payout: bet.potentialPayout,
      currency: bet.currency
    })

    // fetch new balance
    void updateBalance()
  }

export const handleBetVoided =
  (actions: Actions, updateBalance: () => Promise<void>) => (data: unknown) => {
    // console.info('[useWebSockets] betVoided', data)

    const betVoidedDTO = (data as { data: { bet: BetDTO; balance: BalanceDTO } }).data
    const bet = BetMap.fromBetDTO(betVoidedDTO.bet)

    datadogLogs.logger.info('bet_voided', {
      user_event: {
        bet: betVoidedDTO.bet,
        balance: betVoidedDTO.balance
      }
    })

    // update state
    actions.player.betCancelled(bet)
    actions.bet.betVoided(bet)
    // actions.player.balanceChanged(betVoidedDTO.balance)

    // emit to parent iframe
    emitBetResult({
      betId: bet.id,
      status: 'voided',
      currency: bet.currency
    })

    // fetch new balance
    void updateBalance()
  }

export const handleBetCashedOut =
  (actions: Actions, updateBalance: () => Promise<void>) => (data: unknown) => {
    // console.info('[useWebSockets] betCashedOut', data)

    // The Go backend sends cashoutMultiplier and actualPayout at the top level of data,
    // not inside the bet object
    const payload = (
      data as {
        data: {
          bet: BetDTO
          balance: BalanceDTO
          cashoutMultiplier?: number
          actualPayout?: number
        }
      }
    ).data

    datadogLogs.logger.info('bet_cashed_out', {
      user_event: {
        bet: payload.bet,
        balance: payload.balance,
        cashoutMultiplier: payload.cashoutMultiplier,
        actualPayout: payload.actualPayout
      }
    })

    // Map bet from DTO
    const bet = BetMap.fromBetDTO(payload.bet)

    // Extract crash-specific fields from the payload (top level, not inside bet)
    const cashoutMultiplier = payload.cashoutMultiplier ?? payload.bet.cashoutMultiplier ?? 0
    const actualPayoutCents = payload.actualPayout ?? payload.bet.actualPayout ?? 0

    // Convert payout from cents to decimal for display
    const payoutDecimal = fromInteger(actualPayoutCents, payload.bet.currency)

    // Update player balance FIRST (before bet status changes to CASHED_OUT)
    // The player store's betCashedOut has a double-processing guard that checks
    // if the bet is already CASHED_OUT — so it must run before bet.betCashedOut
    // sets the status. This matches the order used in handleBetWon.
    actions.player.betCashedOut(bet, payoutDecimal)

    // Update bet state with cashout info
    actions.bet.betCashedOut(payload.bet.id, cashoutMultiplier, actualPayoutCents)

    // emit to parent iframe
    emitBetResult({
      betId: payload.bet.id,
      status: 'cashed_out',
      payout: payoutDecimal,
      currency: payload.bet.currency,
      multiplier: cashoutMultiplier
    })
    emitCashoutCompleted({
      betId: payload.bet.id,
      multiplier: cashoutMultiplier,
      payout: payoutDecimal,
      currency: payload.bet.currency
    })

    // Fetch new balance as fallback/verification
    void updateBalance()
  }

// RACE CONFIGURATION

const CustomEventDTOSchema = z.object({
  action: z.string(),
  payload: z.record(z.string(), z.unknown())
})

export const handleRaceConfigurationCreated =
  (actions: Actions, gameId?: string) => (data: unknown) => {
    // validate data
    const event = CustomEventDTOSchema.extend({
      action: z.literal('race-configuration-created'),
      payload: SupabaseInsertEventDTOSchema.extend({
        new: RaceConfigurationRowDTOSchema
      })
    }).parse(data)

    // Ignore race configuration not for this game
    if (gameId && event.payload.new.id !== gameId) {
      return
    }

    // console.info('[useWebSockets] race-configuration-created', data)

    // Update game
    const partialGame = GameMap.fromGameRowDTO(event.payload.new)
    actions.game.updateGameById(partialGame)
  }

export const handleRaceConfigurationUpdated =
  (actions: Actions, gameId?: string) => (data: unknown) => {
    // validate data
    const event = CustomEventDTOSchema.extend({
      action: z.literal('race-configuration-updated'),
      payload: SupabaseUpdateEventDTOSchema.extend({
        new: RaceConfigurationRowDTOSchema
      })
    }).parse(data)

    // Ignore race configuration not for this game
    if (gameId && event.payload.new.id !== gameId) {
      return
    }

    // console.info('[useWebSockets] race-configuration-updated', data)

    // Update game
    const partialGame = GameMap.fromGameRowDTO(event.payload.new)
    actions.game.updateGameById(partialGame)
  }

// RACE

export const handleRaceCreated = (actions: Actions, gameId?: string) => (data: unknown) => {
  // validate data
  const event = CustomEventDTOSchema.extend({
    action: z.literal('race-created'),
    payload: SupabaseInsertEventDTOSchema.extend({
      new: RaceRowDTOSchema
    })
  }).parse(data)

  // Ignore race not for this game
  if (gameId && event.payload.new.race_configuration_id !== gameId) {
    // console.info('[useWebSockets] NO MATCHING GAME ID - race-created', data)
    return
  }

  // console.info('[useWebSockets] race-created', data)

  // Create new race
  const newRace = RaceMap.fromRaceRowDTO(event.payload.new)
  actions.game.raceAdded(newRace)
}

export const handleRaceUpdated = (actions: Actions, gameId?: string) => (data: unknown) => {
  // validate data
  const event = CustomEventDTOSchema.extend({
    action: z.literal('race-updated'),
    payload: SupabaseUpdateEventDTOSchema.extend({
      new: RaceRowDTOSchema
    })
  }).parse(data)

  // Ignore race not for this game
  if (gameId && event.payload.new.race_configuration_id !== gameId) {
    // console.info('[useWebSockets] NO MATCHING GAME ID - race-updated', data)
    return
  }

  // console.info('[useWebSockets] race-updated', data)

  // Update race
  actions.game.raceUpdated(RaceMap.fromRaceRowDTO(event.payload.new))
}

// LIGHTNING REVEAL — chip-placement / Evolution-style flow
//
// Backend gates lightning fields out of `race:updated` broadcasts for
// chip-placement-mode players pre-reveal, then fires this dedicated
// `lightning:revealed` event the moment betting closes (Round.LightningRevealedAt
// flips). The payload carries the boost data so we can patch it onto
// the local race; the chip-placement panel's existing reveal-animation
// derives from `currentRace.lightningBoostedItems` becoming populated,
// so simply merging these fields kicks off the strike + indicator
// animations.
//
// Classic-mode clients still get the lightning fields via the normal
// `race:updated` path during betting, so this event is a no-op for
// them (the merge is idempotent — same fields, same values).
const LightningRevealedDTOSchema = z.object({
  roundId: z.string(),
  gameId: z.string(),
  boostType: z.enum(['common', 'rare', 'epic', 'legendary']),
  boostPercent: z.number(),
  // Go marshals `map[string]bool` as a JSON object; we transform to
  // the `string[]` shape the frontend Race type uses elsewhere.
  boostedItems: z.record(z.string(), z.boolean()),
  revealedAt: z.string().optional()
})

export const handleLightningRevealed = (actions: Actions, gameId?: string) => (data: unknown) => {
  const parsed = LightningRevealedDTOSchema.safeParse(data)
  if (!parsed.success) {
    console.warn('[handleLightningRevealed] Invalid payload', parsed.error)
    return
  }

  // Filter out events for other games (we get this on a shared
  // race-api-events channel).
  if (gameId && parsed.data.gameId !== gameId) return

  const items = Object.entries(parsed.data.boostedItems)
    .filter(([, v]) => v)
    .map(([k]) => k)

  actions.game.raceUpdated({
    id: parsed.data.roundId,
    isLightningRound: true,
    lightningBoostType: parsed.data.boostType,
    lightningBoostPercent: parsed.data.boostPercent,
    lightningBoostedItems: items
  })

  // The chip-placement payout-strip reversal (i.e. boosting the bet
  // store's stripped potentialPayout values) lives in
  // `useGameListeners` — it watches the current race state and gates
  // on betting-mode === chip-placement, which we don't have access to
  // here. Doing it there means classic-mode rounds (where bets were
  // never stripped) don't accidentally get double-boosted.
}

// RACE EVENT

export const handleRaceEventCreated =
  (actions: Actions, gameId?: string, updateBalance?: () => Promise<void>) => (data: unknown) => {
    // validate data
    const event = CustomEventDTOSchema.extend({
      action: z.literal('race-event-created'),
      payload: SupabaseInsertEventDTOSchema.extend({
        new: RaceEventRowDTOSchema
      })
    }).parse(data)

    // Ignore race event not for this game
    if (gameId && event.payload.new.race_configuration_id !== gameId) {
      // console.info('[useWebSockets] NO MATHCING GAME ID - race-event-created', data)
      return
    }

    // Birdie's live per-putt rows feed the board's own slice and must NEVER
    // enter `raceEvents` (`getLatestEvent` has no type filter). Exact type set
    // only — `race_paused` / `race_resumed` keep flowing on through.
    if (routeBirdieProgressRow(event.payload.new)) return

    const newRaceEvent = RaceEventMap.fromRaceEventRowDTO(
      event.payload.new as Parameters<typeof RaceEventMap.fromRaceEventRowDTO>[0]
    )

    // The hard close's countdown is counted from the moment this page HEARD
    // `race_ready` (`store/raceEventHeard.ts`): written down before the row
    // reaches the store, so every board reads one moment for it.
    if (isHeardRow(newRaceEvent.eventType)) {
      noteRaceEventHeard(
        newRaceEvent.raceId,
        newRaceEvent.eventType,
        typeof performance !== 'undefined' ? performance.now() : Date.now(),
        isRaceReadyEvent(newRaceEvent) && newRaceEvent.payload?.hardClose !== undefined
      )
    }

    actions.game.raceEventAdded(newRaceEvent)

    // Emit round lifecycle events to parent iframe
    const raceGameId = event.payload.new.race_configuration_id
    if (newRaceEvent.eventType === 'race_ready') {
      emitGameReady({ gameId: raceGameId, roundId: newRaceEvent.raceId })
      emitRoundBettingOpen({ roundId: newRaceEvent.raceId, gameId: raceGameId })
    } else if (newRaceEvent.eventType === 'race_starting') {
      // A hard round posts the close at its board's LOCK, which can be its
      // countdown, ahead of this row (`hooks/useBetLock.ts`): posted once
      // between them. Every other round posts here, as it always has.
      const first = claimBettingClosedPost(newRaceEvent.raceId)
      if (first || !isHardRoundHeard(newRaceEvent.raceId)) {
        emitRoundBettingClosed({ roundId: newRaceEvent.raceId, gameId: raceGameId })
      }
    } else if (newRaceEvent.eventType === 'race_started') {
      const betsAtStart = actions.bet.getBetsByRaceId(newRaceEvent.raceId)
      const totalBetAtStart = betsAtStart.reduce((sum, b) => sum + b.betAmount, 0)
      const currencyAtStart = betsAtStart[0]?.currency
      emitRoundStarted({
        roundId: newRaceEvent.raceId,
        gameId: raceGameId,
        ...(totalBetAtStart > 0 && { totalBetAmount: totalBetAtStart, currency: currencyAtStart })
      })
    } else if (newRaceEvent.eventType === 'race_ended') {
      const betsAtEnd = actions.bet.getBetsByRaceId(newRaceEvent.raceId)
      const totalBetAtEnd = betsAtEnd.reduce((sum, b) => sum + b.betAmount, 0)
      // Only bets the player actually WON contribute to a win total. Summing
      // potentialPayout unconditionally counted losers too — and for a crash bet
      // potentialPayout is the operator LIABILITY CEILING (CrashPotentialPayout
      // ignores the stake), so a single lost 4.20 bet reported a 250,000 win to
      // the operator's lobby. BetMap.fromBetDTO overwrites potentialPayout with
      // the realized actualPayout on cash-out, so the won/cashed_out arms carry
      // the true paid amount for crash and fixed odds for marble.
      const totalWinAtEnd = betsAtEnd.reduce(
        (sum, b) =>
          b.status === 'won' || b.status === 'cashed_out' ? sum + b.potentialPayout : sum,
        0
      )
      const currencyAtEnd = betsAtEnd[0]?.currency
      emitRoundEnded({
        roundId: newRaceEvent.raceId,
        gameId: raceGameId,
        crashMultiplier:
          'payload' in newRaceEvent
            ? (newRaceEvent.payload as { crash_multiplier?: number })?.crash_multiplier
            : undefined,
        ...(totalBetAtEnd > 0 && {
          totalBetAmount: totalBetAtEnd,
          totalWinAmount: totalWinAtEnd,
          currency: currencyAtEnd
        })
      })
    }

    // Clear streak when new round starts - ensures UI is fresh for each round
    // calculatePayout will re-populate with accurate streak data when user selects a bet
    if (newRaceEvent.eventType === 'race_ready') {
      actions.streak?.clearStreak()
    }

    if (isRaceSettledEvent(newRaceEvent)) {
      // Fire-and-forget: settlement may await an authoritative bets:get to
      // reconcile unacknowledged bets before purging (see settleBetsForRace).
      void settleBetsForRace(actions, newRaceEvent, updateBalance, fetchServerBetsSnapshot)
    }

    if (isRaceCancelledEvent(newRaceEvent)) {
      const bets = actions.bet.getBetsByRaceId(newRaceEvent.raceId)

      // Optimistic update
      bets.forEach((bet) => {
        actions.player.betCancelled(bet)
        actions.bet.betCancelled(bet)
      })
    }
  }

export const handleRaceEventUpdated = (actions: Actions, gameId?: string) => (data: unknown) => {
  // validate data
  const event = CustomEventDTOSchema.extend({
    action: z.literal('race-event-updated'),
    payload: SupabaseUpdateEventDTOSchema.extend({
      new: RaceEventRowDTOSchema
    })
  }).parse(data)

  // Ignore race event not for this game
  if (gameId && event.payload.new.race_configuration_id !== gameId) {
    return
  }

  // console.info('[useWebSockets] race-event-updated', data)

  const raceEvent = RaceEventMap.fromRaceEventRowDTO(
    event.payload.new as Parameters<typeof RaceEventMap.fromRaceEventRowDTO>[0]
  )
  actions.game.raceEventUpdated(raceEvent)
}

// TRACK

export const handleTrackCreated = (_actions: Actions) => (data: unknown) => {
  // console.info('[useWebSockets] track-created', data)
}

export const handleTrackUpdated = (actions: Actions) => (data: unknown) => {
  // console.info('[useWebSockets] track-updated', data)

  // validate data
  const event = CustomEventDTOSchema.extend({
    action: z.literal('track-updated'),
    payload: SupabaseUpdateEventDTOSchema.extend({
      old: TrackRowDTOSchema,
      new: TrackRowDTOSchema
    })
  }).parse(data)

  // Update track
  actions.game.updateTrack(event.payload.new)
}

// TRACK MODE

export const handleTrackModeCreated = (_actions: Actions) => (data: unknown) => {
  // console.info('[useWebSockets] track-mode-created', data)
}

export const handleTrackModeUpdated = (actions: Actions) => (data: unknown) => {
  // console.info('[useWebSockets] track-mode-updated', data)

  const event = CustomEventDTOSchema.extend({
    action: z.literal('track-mode-updated'),
    payload: SupabaseUpdateEventDTOSchema.extend({
      old: TrackModeRowDTOSchema,
      new: TrackModeRowDTOSchema
    })
  }).parse(data)

  // Update track mode
  actions.game.updateTrackMode(event.payload.new)
}

// MARBLE

export const handleMarbleCreated = (_actions: Actions, _gameId?: string) => (data: unknown) => {
  // console.info('[useWebSockets] marble-created', data)
}

export const handleMarbleUpdated = (_actions: Actions, _gameId?: string) => (data: unknown) => {
  // console.info('[useWebSockets] marble-updated', data)
}

export const handleMarbleDeleted = (_actions: Actions, _gameId?: string) => (data: unknown) => {
  // console.info('[useWebSockets] marble-deleted', data)
}

export const handleSocialBetReceived = (actions: Actions) => (data: unknown) => {
  // console.info('[useWebSockets] socialBetReceived', data)

  const socialBetDTO = (data as { data: SocialBetDTO }).data
  const socialBet = SocialBetMap.fromSocialBetDTO(socialBetDTO)
  const roundInfo = SocialBetMap.fromSocialBetDTOToRoundInfo(socialBetDTO)

  // v49 — ALWAYS dispatch; the social store owns round matching now.
  // The old `raceId === socialBet.raceId` gate here compared against the
  // CLIENT's lagging round id and silently dropped every bet broadcast
  // before the new round row propagated to this client — the busiest
  // window of the round (auto-rebets at betting open), worst on mobile.
  // The store buffers unknown-round bets and drains them on raceChanged.
  actions.social.betReceived(socialBet, {
    totalBets: roundInfo.totalBets,
    totalAmount: roundInfo.totalAmount
  })
}

// SOCIAL — Round snapshot (pushed on channel join for late joiners)
export const handleRoundSnapshot = (actions: Actions) => (data: unknown) => {
  const snapshot = data as RoundSnapshotDTO
  if (!snapshot?.bets || !Array.isArray(snapshot.bets)) return

  const socialBets = snapshot.bets.map(SocialBetMap.fromSnapshotItemDTO)
  actions.social.setRoundSnapshot(socialBets)
}

// SOCIAL — Bet won outcome (broadcast to social channel)
export const handleSocialBetWon = (actions: Actions) => (data: unknown) => {
  const dto = data as SocialBetSnapshotItemDTO
  if (!dto?.betId) return

  const payout = dto.actualPayout ? fromInteger(dto.actualPayout, 'USD') : undefined
  // The payload is a FULL bet record, so pass it as the upsert fallback: if
  // this client never received the original `bet` broadcast, this is the one
  // repair signal the server guarantees to send.
  actions.social.updateBetStatus(
    dto.betId,
    'won',
    undefined,
    payout,
    SocialBetMap.fromSnapshotItemDTO(dto)
  )
}

// SOCIAL — Bet cashed out outcome (broadcast to social channel)
export const handleSocialBetCashedOut = (actions: Actions) => (data: unknown) => {
  const dto = data as SocialBetSnapshotItemDTO
  if (!dto?.betId) return

  const payout = dto.actualPayout ? fromInteger(dto.actualPayout, 'USD') : undefined
  actions.social.updateBetStatus(
    dto.betId,
    'cashed_out',
    dto.cashoutMultiplier ?? undefined,
    payout,
    SocialBetMap.fromSnapshotItemDTO(dto)
  )
}

export const handleStreamWarning =
  (actions: Actions, playerId: string, gameId: string) => (data: unknown) => {
    // console.info('[useWebSockets] stream-warning', data)
    const isIdleDisabled = isIdleBannerDisabled()
    if (isIdleDisabled) return

    actions.ui.setIdleState('warning')
    emitPlayerIdleWarning({ gameId })

    const bets = getBetsByGameAndPlayer(useBetStore.getState(), gameId, playerId)
    datadogLogs.logger.info('idle_time', {
      user_event: {
        type: 'idle_warning_triggered',
        gameId,
        playerId,
        hasBets: bets.length > 0
      }
    })
  }

export const handleStreamPaused =
  (actions: Actions, playerId: string, gameId: string) => (data: unknown) => {
    // console.info('[useWebSockets] stream-paused', data)
    const isIdleDisabled = isIdleBannerDisabled()
    if (isIdleDisabled) return

    actions.ui.setIdleState('paused')
    emitPlayerIdlePaused({ gameId })

    const bets = getBetsByGameAndPlayer(useBetStore.getState(), gameId, playerId)
    datadogLogs.logger.info('idle_time', {
      user_event: {
        type: 'stream_paused',
        gameId,
        playerId,
        hasBets: bets.length > 0
      }
    })
  }

// GAME CONFIG
const BetLimitsSchema = z.object({
  min: z.number(),
  max: z.number()
})

const MarketOptionSchema = z.object({
  id: z.string(),
  name: z.string().optional().default(''),
  color: z.string().optional().default(''),
  image: z.string().optional(),
  multiplier: z.number(),
  bettable: z.boolean()
})

const MarketConfigSchema = z.object({
  id: z.string(),
  type: z.enum(['main', 'side_bet']),
  label: z.string(),
  description: z.string().optional(),
  probabilities: z.record(z.number()),
  limits: z.object({ PickWinner: BetLimitsSchema }),
  maxMultiplier: z.number(),
  options: z.array(MarketOptionSchema),
  displayOrder: z.number()
})

const GameConfigDTOSchema = z.object({
  usdRate: z.number().optional(),
  bettingMode: z.enum(['classic', 'chip-placement']).optional(),
  markets: z.array(MarketConfigSchema).optional(),
  bet: z.object({
    // chip-placement games legitimately send null/missing for the top-level
    // bet config (per-market configs carry their own probabilities/limits).
    // Transform those into safe defaults so the parsed type matches the
    // `GameConfigDTO` interface and downstream consumers don't have to
    // handle null/undefined for what is structurally a non-nullable field.
    probabilities: z
      .record(z.number())
      .nullish()
      .transform((v) => v ?? {}),
    currency: z.string(),
    maxBetAmount: z.number(),
    minBetAmount: z.number(),
    maxMultiplier: z.number(),
    defaultBetMultiplier: z.number().optional().nullable(),
    chipMultipliers: z.array(z.number()).optional().nullable(),
    maxPayout: z.number(),
    limits: z
      .object({
        PickWinner: BetLimitsSchema,
        ReverseForecast: BetLimitsSchema.optional(),
        StraightForecast: BetLimitsSchema.optional(),
        CombinationTricast: BetLimitsSchema.optional(),
        Tricast: BetLimitsSchema.optional()
      })
      .nullish()
      .transform((v) => v ?? { PickWinner: { min: 0, max: 0 } })
  })
})

const GameConfigErrorDTOSchema = z.object({
  error: z.string()
})

const GameConfigResponseSchema = z.union([GameConfigDTOSchema, GameConfigErrorDTOSchema])

// CRASH OCCURRED - Crash game specific event
// Broadcast when the crash multiplier is reached, allowing immediate UI update
export const handleCrashOccurred = (actions: Actions, gameId?: string) => (data: unknown) => {
  // Validate data structure
  const crashData = data as {
    gameId?: string
    roundId?: string
    crashMultiplier?: number
    crashedAt?: string
  }

  // Ignore events for other games
  if (gameId && crashData.gameId !== gameId) {
    return
  }

  datadogLogs.logger.info('crash_occurred', {
    user_event: {
      gameId: crashData.gameId,
      roundId: crashData.roundId,
      crashMultiplier: crashData.crashMultiplier,
      crashedAt: crashData.crashedAt
    }
  })

  // Mark pending bets for this round as crashed/lost
  // This provides immediate feedback without waiting for race_ended/settlement
  if (crashData.roundId) {
    const bets = actions.bet.getBetsByRaceId(crashData.roundId)
    bets.forEach((bet) => {
      // Only mark pending bets as lost - cashed out bets already have their final state
      if (bet.status === 'pending') {
        actions.bet.betLost(bet)
      }
    })

    // Add a synthetic race_crashed event to stop the multiplier animation
    // and update the UI immediately without waiting for race_ended.
    //
    // The broadcast's crashMultiplier is attached VERBATIM — and only when
    // it is a usable crash point (finite, ≥ 1). Never fabricate a fallback
    // (the old `|| 1.0`): a made-up 1.0 is a valid-looking M the hero stamp
    // would freeze on ("CRASHED @ 1.00x") — the stamp-flash bug's worst
    // form. With no multiplier attached, the crash surfaces treat M as
    // unrevealed and hold the last live value until race_ended delivers it.
    const m = crashData.crashMultiplier
    const crashedEvent = {
      uuid: `crashed-${crashData.roundId}-${Date.now()}`,
      raceId: crashData.roundId,
      eventType: 'race_crashed',
      eventTimestamp: crashData.crashedAt || new Date().toISOString(),
      ...(typeof m === 'number' && Number.isFinite(m) && m >= 1
        ? { payload: { crashMultiplier: m } }
        : {})
    }
    actions.game.raceEventAdded(crashedEvent)
  }
}

export const handleGameConfig =
  (actions: Actions, router: AppRouterInstance) => (data: unknown) => {
    // console.info('[useWebSockets] game-config', data)

    const validationResult = GameConfigResponseSchema.safeParse(data)

    if (!validationResult.success) {
      console.error('[useWebSockets] game-config validation failed', validationResult.error)
      return
    }

    const validData = validationResult.data
    if ('error' in validData) {
      // console.info('No game config,redirecting to lobby')
      // Redirect to lobby
      router.replace(lobbyPath())
    } else {
      const gameConfig = GameConfigDTOSchema.parse(validData)
      actions.game.setGameConfig(gameConfig)
    }
  }

// STREAM CONFIG
const socialStreamSchema = z.object({
  created_at: z.string(),
  id: z.string(),
  stream_id: z.string(),
  title: z.string(),
  live: z.boolean(),
  image_url: z.string().url()
})

// SOCIAL STREAMING CONFIG
const SocialStreamsDTOSchema = z.object({ socialStreams: z.array(socialStreamSchema).optional() })

export const handleSocialStreams = (actions: Actions) => (data: unknown) => {
  // console.info('[useWebSockets] social-streams', data)
  const validationResult = SocialStreamsDTOSchema.safeParse(data)

  if (!validationResult.success) {
    console.error('[useWebSockets] social-streams validation failed', validationResult.error)
    return
  }

  const validData = validationResult.data
  const socialStreams = validData.socialStreams?.map(SocialStreamMap.fromSocialStreamDTO)

  actions.socialStream.setSocialStreams(socialStreams)
}

export const StreaksMessageDTOSchema = z.object({
  streaks: z.array(
    z
      .object({
        type: z.string() // Minimal info required to route correctly.
      })
      .passthrough()
  )
})

export const handleStreaks = (actions: Actions) => (data: unknown) => {
  const result = StreaksMessageDTOSchema.safeParse(data)

  if (!result.success) {
    console.error('[useWebSockets] Invalid streaks payload:', result.error)
    return
  }

  for (const streak of result.data.streaks) {
    switch (streak.type) {
      case 'daily_streak': {
        const parsed = DailyStreakSchema.safeParse(streak)

        if (!parsed.success) {
          console.error('[dailyCheckIn] Invalid streak payload', parsed.error)
          return
        }

        const parsedStreak = fromDailyStreakDTO(parsed.data)
        actions.dailyCheckin.setDailyCheckInData(parsedStreak)
        break
      }
      // Example of other streak types that could be handled
      case 'losing_bet_streak': {
        // handleLosingStreak(streak)
        break
      }
      default:
        console.warn('[useWebSockets] Unknown streak type:', streak.type)
    }
  }
}

export const handleStreakUpdated = (actions: Actions) => (data: unknown) => {
  // console.info('[useWebSockets] streak-updated', data)
  // Handle the streak:updated event which comes in a different format than streaks
  const result = z
    .object({
      data: z.object({
        streak: DailyStreakSchema
      })
    })
    .safeParse(data)

  if (!result.success) {
    console.error('[useWebSockets] Invalid streak:updated payload:', result.error)
    return
  }

  const parsedStreak = fromDailyStreakDTO(result.data.data.streak)
  actions.dailyCheckin.setDailyCheckInData(parsedStreak)
}

// INVENTORY
export const handleInventoryItemAdded =
  (actions: Actions, t?: (key: string) => string) => (data: unknown) => {
    // Validate the incoming data
    const result = InventoryItemDtoSchema.safeParse(data)

    if (!result.success) {
      console.error('[useWebSockets] Invalid inventory item payload:', result.error)
      return
    }

    // Map DTO to domain model
    const inventoryItem = fromInventoryItemDTO(result.data)

    // Add item to inventory store
    if (actions.inventory) {
      actions.inventory.addInventoryItem(inventoryItem)
    }

    // Show notification using the generic notification system
    if (actions.notification && inventoryItem.status !== 'claimable') {
      // Use provided translation function or default to key
      const translate = t || ((key: string) => key)

      // For claimable items, provide claim function
      const claimFunction = actions.inventory
        ? async (itemId: string) => {
            actions.inventory!.claimItem(itemId)
          }
        : undefined

      const notificationItem = inventoryItemToNotification(inventoryItem, translate, claimFunction)
      actions.notification.addNotification(notificationItem)
    }

    // console.info('[useWebSockets] Inventory item added:', inventoryItem)
  }

export const handleInventoryItemUpdated =
  (actions: Actions, t?: (key: string) => string) => (data: unknown) => {
    // Validate the incoming data
    const result = InventoryItemDtoSchema.safeParse(data)
    if (!result.success) {
      console.error('[useWebSockets] Invalid inventory item payload:', result.error)
      return
    }
    // Map DTO to domain model
    const inventoryItem = fromInventoryItemDTO(result.data)
    // Update item in inventory store
    if (actions.inventory) {
      actions.inventory.updateInventoryItem(inventoryItem)
    }

    // console.info('[useWebSockets] Inventory item updated:', inventoryItem)
  }

// PLAYER LEVEL
// Note: This event is sent when player levels up, not for regular stats updates
const PlayerLevelUpdatedSchema = z.object({
  data: z.object({
    newLevel: z.number(),
    totalWageredUSD: z.number(), // XP in cents from backend
    multiplierBonus: z.number()
  })
})

export const handlePlayerLevelUpdated =
  (actions: Actions, t?: (key: string) => string) => (data: unknown) => {
    const result = PlayerLevelUpdatedSchema.safeParse(data)

    if (!result.success) {
      console.error('[useWebSockets] Invalid player level payload:', result.error)
      return
    }

    const levelData = result.data.data

    if (actions.playerLevel) {
      // Mark the level system as enabled since we received level data
      actions.playerLevel.setLevelSystemEnabled(true)
      actions.playerLevel.setPlayerLevel({
        level: levelData.newLevel,
        xp: levelData.totalWageredUSD, // Keep as cents, displayed as unitless XP
        multiplierBonus: levelData.multiplierBonus
      })
    }

    // Show level-up notification
    if (actions.notification) {
      // Use provided translation function or default to key
      const translate = t || ((key: string) => key)
      const notificationItem = levelUpToNotification(
        levelData.newLevel,
        levelData.multiplierBonus,
        translate
      )
      actions.notification.addNotification(notificationItem)
    }

    // console.info('[useWebSockets] Player level updated:', levelData)
  }

// PLAYER STATS UPDATE
// This event is sent after every bet settlement with updated stats
const PlayerStatsUpdatedSchema = z.object({
  data: z.object({
    // Level info
    level: z.number(),
    xp: z.number(), // XP in cents (total wagered USD)
    multiplierBonus: z.number(),
    // Stats info
    totalBets: z.number(),
    totalWins: z.number(),
    winRate: z.number(),
    totalWonUSD: z.number(), // In cents
    // Currency-converted values (optional)
    totalWagered: z.number().optional(),
    totalWon: z.number().optional(),
    biggestWin: z.number().optional(),
    currency: z.string().optional()
  })
})

export const handlePlayerStatsUpdated =
  (actions: Actions, t?: (key: string) => string) => (data: unknown) => {
    const result = PlayerStatsUpdatedSchema.safeParse(data)

    if (!result.success) {
      console.error('[useWebSockets] Invalid player stats payload:', result.error)
      return
    }

    const statsData = result.data.data

    if (actions.playerLevel) {
      // Get current level BEFORE updating to detect level-up
      const previousLevel = usePlayerLevelStore.getState().playerLevel?.level ?? 0

      // Update level info
      actions.playerLevel.setLevelSystemEnabled(true)
      actions.playerLevel.setPlayerLevel({
        level: statsData.level,
        xp: statsData.xp,
        multiplierBonus: statsData.multiplierBonus
      })

      // Update stats info
      actions.playerLevel.setPlayerStats({
        totalBets: statsData.totalBets,
        totalWins: statsData.totalWins,
        winRate: statsData.winRate,
        totalWagered: statsData.totalWagered,
        totalWon: statsData.totalWon,
        biggestWin: statsData.biggestWin,
        currency: statsData.currency
      })

      // Detect level-up and show notification
      if (statsData.level > previousLevel && previousLevel > 0) {
        if (actions.notification) {
          // Use provided translation function or default to key
          const translate = t || ((key: string) => key)
          const notificationItem = levelUpToNotification(
            statsData.level,
            statsData.multiplierBonus,
            translate
          )
          actions.notification.addNotification(notificationItem)
        }
      }
    }

    // console.info('[useWebSockets] Player stats updated:', statsData)
  }
